import { httpClient } from "@/lib/http";
import { DASHBOARD_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/lib/http";

// ============================================================================
// Response Types
// ============================================================================

/**
 * User statistics from /admin/users/stats
 */
export type UserStats = {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    suspendedUsers: number;
    deletedUsers: number;
    totalRiders: number;
    totalDrivers: number;
    totalAdmins: number;
    emailVerifiedUsers: number;
    phoneVerifiedUsers: number;
    registrationsLast7Days: number;
    activeDrivers: number;
    inactiveDrivers: number;
    verifiedDrivers: number;
    newDriversWeekToDate: number;
    activeRiders: number;
    inactiveRiders: number;
    verifiedRiders: number;
    newRidersWeekToDate: number;
};

/**
 * Ride statistics from /admin/rides/stats
 */
export type RideStats = {
    // Ride offer counts
    totalRideOffers: number;
    availableRides: number;
    fullRides: number;
    inProgressRides: number;
    completedRides: number;
    cancelledRides: number;
    expiredRides: number;
    ridesLast7Days: number;
    ridesLast30Days: number;
    // Booking counts
    totalBookings: number;
    pendingBookings: number;
    confirmedBookings: number;
    completedBookings: number;
    cancelledBookings: number;
    expiredNoShowBookings: number;
    bookingsLast7Days: number;
    bookingsLast30Days: number;
    // Revenue from bookings (NGN)
    totalRevenueFromBookings: number;
    revenueLast7Days: number;
    revenueLast30Days: number;
};

/**
 * Trip statistics from /admin/trips/stats
 */
export type TripStats = {
    // Trip counts by status
    totalTrips: number;
    totalBookings: number;
    pendingTrips: number;
    startedTrips: number;
    inProgressTrips: number;
    completedTrips: number;
    cancelledTrips: number;
    tripsLast7Days: number;
    tripsLast30Days: number;
    // Marketplace efficiency
    rideMatchRatePct: number | null;
    driverAcceptanceRatePct: number | null;
    riderSuccessRatePct: number | null;
    averageSeatsFilled: number | null;
    seatUtilizationRatePct: number | null;
    averageMatchingTimeMinutes: number | null;
    // Ratings
    avgDriverRating: number;
    avgPassengerRating: number;
    tripsRatedByPassenger: number;
    tripsRatedByDriver: number;
    // Fare revenue (NGN)
    totalFareRevenue: number;
    fareRevenueLast7Days: number;
    fareRevenueLast30Days: number;
};

/**
 * Wallet & Financial statistics from /admin/wallet/stats
 */
export type WalletStats = {
    // Wallet counts
    totalWallets: number;
    activeWallets: number;
    frozenWallets: number;
    closedWallets: number;
    totalBalanceInCirculation: number;
    // Transaction counts
    totalTransactions: number;
    totalCreditTransactions: number;
    totalDebitTransactions: number;
    // Transaction volumes (NGN)
    totalCreditVolume: number;
    totalDebitVolume: number;
    creditVolumeLast7Days: number;
    debitVolumeLast7Days: number;
    creditVolumeLast30Days: number;
    debitVolumeLast30Days: number;
    // Top-up volumes (NGN)
    totalTopUpVolume: number;
    topUpVolumeLast7Days: number;
    topUpVolumeLast30Days: number;
    // Withdrawal volumes (NGN)
    totalWithdrawalVolume: number;
    withdrawalVolumeLast7Days: number;
    withdrawalVolumeLast30Days: number;
    // Commission & Refunds (NGN)
    totalCommissions: number;
    commissionsLast7Days: number;
    commissionsLast30Days: number;
    totalRefunds: number;
    refundsLast7Days: number;
    // Payouts
    totalPayouts: number;
    pendingPayouts: number;
    completedPayouts: number;
    failedPayouts: number;
    totalPayoutAmount: number;
    payoutsLast7Days: number;
    payoutsLast30Days: number;
    // Admin financial dashboard fields
    totalWalletFunded: number;
    platformCommission: number;
    avgRidePrice: number | null;
    walletBalances: number;
    pendingDriverPayoutAmount: number;
    completedPayoutAmount: number;
    totalWalletFundedWtd: number;
    platformCommissionWtd: number;
    totalPayoutWtd: number;
};

/**
 * Combined dashboard statistics
 */
export type DashboardStats = {
    users: UserStats;
    rides: RideStats;
    trips: TripStats;
    wallet: WalletStats;
};

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get user statistics
 */
export const getUserStats = async () => {
    const { data } = await httpClient.get<ApiResponse<UserStats>>(DASHBOARD_ENDPOINTS.USER_STATS);
    return data;
};

/**
 * Get ride and booking statistics
 */
export const getRideStats = async () => {
    const { data } = await httpClient.get<ApiResponse<RideStats>>(DASHBOARD_ENDPOINTS.RIDE_STATS);
    return data;
};

/**
 * Get trip and rating statistics
 */
export const getTripStats = async () => {
    const { data } = await httpClient.get<ApiResponse<TripStats>>(DASHBOARD_ENDPOINTS.TRIP_STATS);
    return data;
};

/**
 * Get wallet and financial statistics
 */
export const getWalletStats = async () => {
    const { data } = await httpClient.get<ApiResponse<WalletStats>>(DASHBOARD_ENDPOINTS.WALLET_STATS);
    return data;
};

/**
 * Get all dashboard statistics in parallel
 */
export const getAllDashboardStats = async (): Promise<DashboardStats> => {
    const [usersResponse, ridesResponse, tripsResponse, walletResponse] = await Promise.all([
        getUserStats(),
        getRideStats(),
        getTripStats(),
        getWalletStats(),
    ]);

    return {
        users: usersResponse.data,
        rides: ridesResponse.data,
        trips: tripsResponse.data,
        wallet: walletResponse.data,
    };
};
